import { DrawerContent } from "@chakra-ui/react";
import React, { memo, useCallback, useMemo, useState } from "react";
import useMessageThreadDraft from "modules/hooks/use-message-thread-draft";
import { HAPPYCO_THREAD_DRAFT_ID } from "modules/utils/Constants";
import { useDropzone } from "react-dropzone";
import { useFile } from "modules/providers/file-provider";
import { usePanelSize } from "modules/providers/panel-size-provider";
import DrawerTopHeader from "modules/drawers/drawer-top-header";
import { HappycoLogoIcon } from "modules/icons";
import colors from "app/colors";
import DropFilesHere from "modules/messaging/_shared/drop-files-here";
import { MessageFragment, MessageThreadFragment } from "gql/graphql";
import { useApolloClient } from "@apollo/client";
import MESSAGE_THREAD_FRAGMENT from "modules/messaging/_networking/message-thread-fragment";
import { useUsers } from "modules/providers/users-provider";
import { getUserFullName } from "modules/utils/Functions";
import ForwardToSelectMessages from "modules/drawers/forward-to/forward-to-select-messages";
import HappycoExportToExistingWorkOrderForm from "./happyco-export-to-existing-work-order-form";
import DeltaBuilder from "modules/quill/delta-builder";
import { isEmpty } from "lodash";

type HAPPYCO_EXPORT_TO_EXISTING_WORK_ORDER_STATE =
  | "select-messages"
  | "export-to-existing-work-order";

const HappycoExportToExistingWorkOrderDrawer = () => {
  const { uploadFiles } = useFile();
  const { rightWidth } = usePanelSize();
  const { cache } = useApolloClient();
  const { usersById } = useUsers();
  const { draft, setDelta } = useMessageThreadDraft({
    draftId: HAPPYCO_THREAD_DRAFT_ID,
  });

  const messageThread = useMemo(() => {
    if (!draft?.relatedMessageThread) return null;
    return cache.readFragment<MessageThreadFragment>({
      id: `MessageThread:${draft.relatedMessageThread.id}`,
      fragment: MESSAGE_THREAD_FRAGMENT,
      fragmentName: "MessageThread",
    });
  }, [cache, draft?.relatedMessageThread]);

  const [state, setState] =
    useState<HAPPYCO_EXPORT_TO_EXISTING_WORK_ORDER_STATE>("select-messages");

  const title = useMemo(() => {
    switch (state) {
      case "select-messages":
        return "Select Messages";
      case "export-to-existing-work-order":
        return "Forward to Work Order";
      default:
        return null;
    }
  }, [state]);

  const onSelectMessagesNextClicked = useCallback(
    async (messages?: MessageFragment[]) => {
      if (messages && messages.length > 0) {
        const builder = new DeltaBuilder();

        messages.forEach((message, index) => {
          const user = usersById[message.createdUserId];
          const text = message.text ?? message.actionText;
          if (user && !isEmpty(text)) {
            builder
              .text(`${getUserFullName(user)}:\n`, { bold: true })
              .text(`"${text}"`);

            if (index < messages.length - 1) {
              builder.newline().newline();
            }
          }
        });
        builder.newline();
        await setDelta(builder);
      }
      setState("export-to-existing-work-order");
    },
    [setDelta, usersById],
  );

  const onFileListUpload = useCallback(
    (files: File[]) => uploadFiles(HAPPYCO_THREAD_DRAFT_ID, files),
    [uploadFiles],
  );

  const { getRootProps, isDragActive } = useDropzone({
    onDrop: onFileListUpload,
    noClick: true,
  });

  const headerIcon = useMemo(() => {
    switch (state) {
      case "export-to-existing-work-order":
        return <HappycoLogoIcon w="80px" h="14px" fill={colors.happycoBlue} />;
      default:
        return null;
    }
  }, [state]);

  if (!draft || !draft.relatedMessageThread || !messageThread) return null;

  return (
    <DrawerContent {...getRootProps()} w={rightWidth} maxW={rightWidth}>
      <DrawerTopHeader title={title} icon={headerIcon} />

      {state === "select-messages" && (
        <ForwardToSelectMessages
          messageThread={messageThread}
          onNextClicked={onSelectMessagesNextClicked}
          draftId={HAPPYCO_THREAD_DRAFT_ID}
        />
      )}

      {state === "export-to-existing-work-order" && (
        <HappycoExportToExistingWorkOrderForm />
      )}

      {state === "export-to-existing-work-order" && (
        <DropFilesHere isDragActive={isDragActive} width={rightWidth} />
      )}
    </DrawerContent>
  );
};

export default memo(HappycoExportToExistingWorkOrderDrawer);
